import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Shield,
  TrendingUp,
  FileCheck,
  Zap,
  CheckCircle2,
  ArrowRight,
  Phone,
  Mail,
  Landmark,
  Car,
  ClipboardCheck,
  BarChart3,
  AlertTriangle,
  Clock,
  Users,
  Globe,
  Database,
  Code,
  Eye,
  Target,
  BadgeCheck,
  Banknote,
  ShieldCheck,
  Gavel,
  ChevronRight,
  Play,
  Menu,
  X,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCurrency } from '../context/CurrencyContext';
import CountrySelector from '../components/CountrySelector';

const Business = () => {
  const { t, selectedCountry } = useCurrency();
  const basePath = `/${selectedCountry.code.toLowerCase()}`;
  const [activeTab, setActiveTab] = useState('banks');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const isIndia = selectedCountry.code === 'IN';
  const resetModal = () => {
    setDemoModalOpen(false);
  };

  const handleDemoClick = () => {
    setDemoModalOpen(true);
  }

  const useCases = {
    banks: {
      title: t('business.banks.title'),
      subtitle: t('business.banks.subtitle'),
      icon: Landmark,
      description: t('business.banks.desc'),
      output: t('business.banks.output'),
      benefits: [
        { icon: Zap, title: t('business.banks.benefits.faster.title'), desc: t('business.banks.benefits.faster.desc') },
        { icon: ShieldCheck, title: t('business.banks.benefits.npa.title'), desc: t('business.banks.benefits.npa.desc') },
        { icon: BarChart3, title: t('business.banks.benefits.valuation.title'), desc: t('business.banks.benefits.valuation.desc') },
        { icon: AlertTriangle, title: t('business.banks.benefits.fraud.title'), desc: t('business.banks.benefits.fraud.desc') },
      ],
      stats: [
        { value: '40%', label: t('business.banks.stats.npa') },
        { value: '3hrs', label: t('business.banks.stats.time') },
        { value: '99.7%', label: t('business.banks.stats.fraud') },
      ]
    },
    insurance: {
      title: t('business.insurance.title'),
      subtitle: t('business.insurance.subtitle'),
      icon: Shield,
      description: t('business.insurance.desc'),
      output: t('business.insurance.output'),
      benefits: [
        { icon: Clock, title: t('business.insurance.benefits.faster.title'), desc: t('business.insurance.benefits.faster.desc') },
        { icon: CheckCircle2, title: t('business.insurance.benefits.disputes.title'), desc: t('business.insurance.benefits.disputes.desc') },
        { icon: Target, title: t('business.insurance.benefits.estimation.title'), desc: t('business.insurance.benefits.estimation.desc') },
        { icon: FileCheck, title: t('business.insurance.benefits.audit.title'), desc: t('business.insurance.benefits.audit.desc') },
      ],
      stats: [
        { value: '60%', label: t('business.insurance.stats.claims') },
        { value: '35%', label: t('business.insurance.stats.savings') },
        { value: '100%', label: t('business.insurance.stats.audit') },
      ]
    },
    auction: {
      title: t('business.auction.title'),
      subtitle: t('business.auction.subtitle'),
      icon: Gavel,
      description: t('business.auction.desc'),
      output: t('business.auction.output'),
      benefits: [
        { icon: Banknote, title: t('business.auction.benefits.pricing.title'), desc: t('business.auction.benefits.pricing.desc') },
        { icon: Users, title: t('business.auction.benefits.disputes.title'), desc: t('business.auction.benefits.disputes.desc') },
        { icon: Eye, title: t('business.auction.benefits.decisions.title'), desc: t('business.auction.benefits.decisions.desc') },
        { icon: Clock, title: t('business.auction.benefits.turnaround.title'), desc: t('business.auction.benefits.turnaround.desc') },
      ],
      stats: [
        { value: '±5%', label: t('business.auction.stats.pricing') },
        { value: '70%', label: t('business.auction.stats.disputes') },
        { value: '2x', label: t('business.auction.stats.clearance') },
      ]
    }
  };

  const enterpriseFeatures = [
    { icon: FileCheck, title: t('businessPage.features.report.title'), desc: t('businessPage.features.report.desc') },
    { icon: Eye, title: t('businessPage.features.damage.title'), desc: t('businessPage.features.damage.desc') },
    { icon: BarChart3, title: t('businessPage.features.valuation.title'), desc: t('businessPage.features.valuation.desc') },
    { icon: AlertTriangle, title: t('businessPage.features.fraud.title'), desc: t('businessPage.features.fraud.desc') },
    { icon: Globe, title: isIndia ? t('businessPage.features.coverage.titleIndia') : t('businessPage.features.coverage.titleMY'), desc: isIndia ? t('businessPage.features.coverage.descIndia') : t('businessPage.features.coverage.descMY') },
    { icon: Code, title: t('businessPage.features.api.title'), desc: t('businessPage.features.api.desc') },
  ];

  const processSteps = [
    { num: 1, title: t('businessPage.howItWorks.steps.0.title'), desc: t('businessPage.howItWorks.steps.0.desc') },
    { num: 2, title: t('businessPage.howItWorks.steps.1.title'), desc: t('businessPage.howItWorks.steps.1.desc') },
    { num: 3, title: t('businessPage.howItWorks.steps.2.title'), desc: t('businessPage.howItWorks.steps.2.desc') },
    { num: 4, title: t('businessPage.howItWorks.steps.3.title'), desc: t('businessPage.howItWorks.steps.3.desc') },
    { num: 5, title: t('businessPage.howItWorks.steps.4.title'), desc: t('businessPage.howItWorks.steps.4.desc') },
  ];

  const trustedBy = isIndia
    ? ['HDFC Bank', 'ICICI Bank', 'Axis Bank', 'Bajaj Finserv', 'ICICI Lombard', 'HDFC Ergo', 'OLX Autos', 'Cars24']
    : ['Maybank', 'CIMB', 'Public Bank', 'Allianz', 'AIA', 'Prudential', 'Carsome', 'myTukar'];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <Link to={basePath} className="flex-shrink-0">
              <img
                src="/wisedrive-logo-new.png"
                alt="Wisedrive"
                className="h-8 md:h-10"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to={basePath} className="text-slate-300 hover:text-white font-medium transition-colors">
                {t('businessPage.nav.consumer')}
              </Link>
              <a href="#use-cases" className="text-slate-300 hover:text-white font-medium transition-colors">
                {t('businessPage.nav.useCases')}
              </a>
              <a href="#features" className="text-slate-300 hover:text-white font-medium transition-colors">
                {t('businessPage.nav.features')}
              </a>
              <a href="#how-it-works" className="text-slate-300 hover:text-white font-medium transition-colors">
                {t('businessPage.nav.howItWorks')}
              </a>
              <CountrySelector />
              <Button onClick={handleDemoClick} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">
                {t('business.requestDemo')}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-white hover:bg-slate-800 transition-colors"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800 border-t border-slate-700">
            <div className="px-4 py-4 space-y-3">
              <Link to={basePath} className="block px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg">
                {t('businessPage.nav.consumer')}
              </Link>
              <a href="#use-cases" className="block px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg">
                {t('businessPage.nav.useCases')}
              </a>
              <a href="#features" className="block px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg">
                {t('businessPage.nav.features')}
              </a>
              <div className="px-4 py-2">
                <CountrySelector />
              </div>
              <Button onClick={handleDemoClick} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                {t('business.requestDemo')}
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 px-4 py-2 rounded-full mb-6">
                <Building2 className="h-4 w-4 text-blue-400" />
                <span className="text-blue-300 font-semibold text-sm">{t('business.heroSubtitle')}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {t('business.heroTitle')}{' '}
                <span className="text-blue-400">{t('business.heroTitleHighlight')}</span>
              </h1>

              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                {t('business.heroDesc')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={handleDemoClick} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg font-bold shadow-lg shadow-blue-600/30 hover:shadow-xl transition-all">
                  {t('business.requestDemo')} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button onClick={handleDemoClick} variant="outline" className="border-2 border-slate-500 text-white hover:bg-slate-800 px-8 py-6 rounded-xl text-lg font-semibold">
                  <Phone className="mr-2 h-5 w-5" /> {t('businessPage.hero.viewCapabilities')}
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: isIndia ? '500+' : '50+', label: t('businessPage.stats.citiesCovered'), icon: Globe },
                { value: isIndia ? '1L+' : '10K+', label: t('businessPage.stats.inspectionsDone'), icon: ClipboardCheck },
                { value: '50+', label: t('businessPage.stats.enterpriseClients'), icon: Building2 },
                { value: '99.7%', label: t('businessPage.stats.aiAccuracy'), icon: Target },
              ].map((stat, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                  <stat.icon className="h-8 w-8 text-blue-400 mb-3" />
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Trusted By */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-center text-sm text-slate-400 mb-6">{t('business.trustedBy').toUpperCase()}</p>
            <div className="flex flex-wrap justify-center gap-6">
              {trustedBy.map((company) => (
                <span key={company} className="px-4 py-2 bg-white/5 rounded-lg text-slate-400 text-sm font-medium">
                  {company}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {t('business.useCasesTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('business.useCasesDesc')}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white rounded-2xl p-2 shadow-lg border border-slate-200">
              {Object.entries(useCases).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${activeTab === key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-slate-600 hover:bg-slate-100'
                    }`}
                >
                  <value.icon className="h-5 w-5" />
                  <span className="hidden sm:inline">{value.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Tab Content */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Left - Description */}
              <div className="p-8 lg:p-12">
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  {React.createElement(useCases[activeTab].icon, { className: "h-4 w-4" })}
                  {useCases[activeTab].subtitle}
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {useCases[activeTab].title}
                </h3>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  {useCases[activeTab].description}
                </p>

                <div className="bg-slate-50 rounded-xl p-4 mb-8">
                  <p className="text-sm text-slate-500 mb-1">{t('businessPage.output')}</p>
                  <p className="font-semibold text-slate-900">{useCases[activeTab].output}</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  {useCases[activeTab].stats.map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{stat.value}</div>
                      <div className="text-xs text-slate-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Benefits */}
              <div className="bg-slate-900 p-8 lg:p-12 text-white">
                <h4 className="text-xl font-bold mb-6">{t('businessPage.benefits.title')}</h4>
                <div className="space-y-4">
                  {useCases[activeTab].benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-4 bg-white/5 rounded-xl p-4">
                      <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-white mb-1">{benefit.title}</h5>
                        <p className="text-sm text-slate-400">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Features */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {t('businessPage.features.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('businessPage.features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enterpriseFeatures.map((feature, idx) => (
              <div key={idx} className="group bg-slate-50 hover:bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
                <div className="h-14 w-14 bg-blue-100 group-hover:bg-blue-600 rounded-xl flex items-center justify-center mb-4 transition-colors">
                  <feature.icon className="h-7 w-7 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accuracy & Technology Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-indigo-500 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 px-4 py-2 rounded-full mb-4">
              <Target className="h-4 w-4 text-emerald-400" />
              <span className="text-emerald-300 font-semibold text-sm">{t('businessPage.trust.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {t('business.trustTitle')}
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('businessPage.trust.descriptionFull')}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { value: '99.7%', label: t('business.trustStats.fraud.label'), desc: t('business.trustStats.fraud.desc') },
              { value: '97.3%', label: t('business.trustStats.damage.label'), desc: t('business.trustStats.damage.desc') },
              { value: '±3%', label: t('business.trustStats.valuation.label'), desc: t('business.trustStats.valuation.desc') },
              { value: '<0.1%', label: t('business.trustStats.accuracy.label'), desc: t('business.trustStats.accuracy.desc') },
            ].map((stat, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center">
                <div className="text-4xl font-bold text-emerald-400 mb-2">{stat.value}</div>
                <div className="font-semibold text-white mb-1">{stat.label}</div>
                <div className="text-xs text-slate-400">{stat.desc}</div>
              </div>
            ))}
          </div>

          {/* Technology Stack */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <h3 className="text-xl font-bold text-center mb-8">{t('business.techTitle')}</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="h-16 w-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-bold text-white mb-2">{t('business.techItems.vision.title')}</h4>
                <p className="text-sm text-slate-400">{t('businessPage.tech.visionDesc')}</p>
              </div>
              <div className="text-center">
                <div className="h-16 w-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Database className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-bold text-white mb-2">{t('business.techItems.obd.title')}</h4>
                <p className="text-sm text-slate-400">{t('businessPage.tech.obdDesc')}</p>
              </div>
              <div className="text-center">
                <div className="h-16 w-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-bold text-white mb-2">{t('business.techItems.valuation.title')}</h4>
                <p className="text-sm text-slate-400">{t('businessPage.tech.valuationDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transparency & Compliance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <ShieldCheck className="h-4 w-4" />
                {t('businessPage.transparency.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {t('business.transparencyTitle')}
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                {t('business.transparencyDesc')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: FileCheck, title: t('business.transparencyItems.timestamp.title'), desc: t('business.transparencyItems.timestamp.desc') },
                  { icon: Users, title: t('business.transparencyItems.inspector.title'), desc: t('business.transparencyItems.inspector.desc') },
                  { icon: Shield, title: t('business.transparencyItems.tamperProof.title'), desc: t('business.transparencyItems.tamperProof.desc') },
                  { icon: BadgeCheck, title: t('businessPage.transparency.regulatory.title'), desc: t('businessPage.transparency.regulatory.desc') },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 bg-slate-50 rounded-xl p-4">
                    <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Report Preview */}
            <div className="bg-slate-900 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-red-500 rounded-full" />
                  <div className="h-3 w-3 bg-yellow-500 rounded-full" />
                  <div className="h-3 w-3 bg-green-500 rounded-full" />
                </div>
                <span className="text-xs text-slate-500">{t('businessPage.reportPreview.sampleReport')}</span>
              </div>
              <div className="bg-white rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-slate-900">{t('businessPage.reportPreview.inspectionReport')}</span>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">{t('businessPage.reportPreview.verified')}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-slate-500">{t('businessPage.reportPreview.reportId')}:</span> <span className="font-mono">INS-2025-001234</span></div>
                  <div><span className="text-slate-500">{t('businessPage.reportPreview.vehicle')}:</span> <span>Honda City VX</span></div>
                  <div><span className="text-slate-500">{t('businessPage.reportPreview.conditionScore')}:</span> <span className="text-emerald-600 font-bold">8.5/10</span></div>
                  <div><span className="text-slate-500">{t('businessPage.reportPreview.riskFlag')}:</span> <span className="text-emerald-600">{t('businessPage.reportPreview.none')}</span></div>
                </div>
              </div>
              <div className="bg-slate-800 rounded-xl p-4 mb-4">
                <div className="text-xs text-slate-400 mb-2">{t('businessPage.reportPreview.auditTrail')}</div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between text-slate-300">
                    <span>{t('businessPage.reportPreview.started')}</span>
                    <span className="text-slate-500">10:30 AM, 27 Jan 2025</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>{t('businessPage.reportPreview.photos')}</span>
                    <span className="text-slate-500">10:32 - 11:15 AM</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>{t('businessPage.reportPreview.obdComplete')}</span>
                    <span className="text-slate-500">11:20 AM</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>{t('businessPage.reportPreview.aiDone')}</span>
                    <span className="text-slate-500">11:45 AM</span>
                  </div>
                  <div className="flex justify-between text-emerald-400">
                    <span>{t('businessPage.reportPreview.generated')}</span>
                    <span className="text-slate-500">11:47 AM</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">GPS: 12.9716° N, 77.5946° E</span>
                <span className="text-emerald-400">✓ {t('businessPage.reportPreview.digitallySigned')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inspector Network Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {isIndia ? t('businessPage.network.titleIndia') : t('businessPage.network.titleMY')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('businessPage.network.subtitle').replace('{count}', isIndia ? '500+' : '50+')}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { value: isIndia ? '2,500+' : '250+', label: t('businessPage.network.stats.inspectors'), icon: Users },
              { value: isIndia ? '500+' : '50+', label: t('businessPage.network.stats.cities'), icon: Globe },
              { value: '4.8/5', label: t('businessPage.network.stats.rating'), icon: BadgeCheck },
              { value: '< 24hrs', label: t('businessPage.network.stats.tat'), icon: Clock },
            ].map((stat, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-200 text-center shadow-sm">
                <stat.icon className="h-10 w-10 text-blue-600 mx-auto mb-3" />
                <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">{t('businessPage.network.process.title')}</h3>
            <div className="grid md:grid-cols-5 gap-4">
              {[
                { num: 1, title: t('businessPage.network.process.steps.0.title'), desc: t('businessPage.network.process.steps.0.desc') },
                { num: 2, title: t('businessPage.network.process.steps.1.title'), desc: t('businessPage.network.process.steps.1.desc') },
                { num: 3, title: t('businessPage.network.process.steps.2.title'), desc: t('businessPage.network.process.steps.2.desc') },
                { num: 4, title: t('businessPage.network.process.steps.3.title'), desc: t('businessPage.network.process.steps.3.desc') },
                { num: 5, title: t('businessPage.network.process.steps.4.title'), desc: t('businessPage.network.process.steps.4.desc') },
              ].map((step, idx) => (
                <div key={idx} className="text-center">
                  <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">
                    {step.num}
                  </div>
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">{step.title}</h4>
                  <p className="text-xs text-slate-500">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {t('businessPage.howItWorks.title')}
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('businessPage.howItWorks.subtitle')}
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-blue-600/30 -translate-y-1/2" />

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {processSteps.map((step, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all">
                    <div className="h-14 w-14 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold relative z-10">
                      {step.num}
                    </div>
                    <h4 className="font-bold text-white mb-2">{step.title}</h4>
                    <p className="text-sm text-slate-400">{step.desc}</p>
                  </div>
                  {idx < processSteps.length - 1 && (
                    <ChevronRight className="hidden lg:block absolute top-1/2 -right-3 h-6 w-6 text-blue-400 -translate-y-1/2 z-20" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* API Integration Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Code className="h-4 w-4" />
                {t('businessPage.api.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {t('businessPage.api.title')}
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                {t('businessPage.api.desc')}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  t('businessPage.api.features.0'),
                  t('businessPage.api.features.1'),
                  t('businessPage.api.features.2'),
                  t('businessPage.api.features.3'),
                  t('businessPage.api.features.4'),
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl font-semibold">
                {t('businessPage.api.viewDocs')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Code Preview */}
            <div className="bg-slate-900 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-3 w-3 bg-red-500 rounded-full" />
                <div className="h-3 w-3 bg-yellow-500 rounded-full" />
                <div className="h-3 w-3 bg-green-500 rounded-full" />
              </div>
              <pre className="text-sm text-slate-300 overflow-x-auto">
                <code>{`// Request Inspection
POST /api/v1/inspections

{
  "vehicle": {
    "registration": "KA01AB1234",
    "type": "car"
  },
  "location": {
    "city": "Bangalore",
    "pincode": "560001"
  },
  "callback_url": "https://your-api.com/webhook"
}

// Response
{
  "inspection_id": "INS-2025-001234",
  "status": "scheduled",
  "estimated_time": "3 hours",
  "inspector": {
    "name": "Rajesh Kumar",
    "rating": 4.9
  }
}`}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {t('businessPage.cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('businessPage.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button onClick={handleDemoClick} className="bg-white text-blue-600 hover:bg-slate-100 px-8 py-6 rounded-xl text-lg font-bold shadow-lg hover:shadow-xl transition-all">
              {t('business.requestDemo')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 rounded-xl text-lg font-semibold">
              <Mail className="mr-2 h-5 w-5" /> {t('businessPage.cta.contact')}
            </Button>
          </div>
          <p className="mt-8 text-blue-200 text-sm">
            {t('business.salesEmail')} | {t('business.salesPhone')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <img
                src="/wisedrive-logo-new.png"
                alt="Wisedrive"
                className="h-8 mb-2"
              />
              <p className="text-slate-400 text-sm">{t('businessPage.footer.tagline')}</p>
            </div>
            <div className="flex gap-6 text-sm text-slate-400">
              <Link to={basePath} className="hover:text-white transition-colors">{t('businessPage.nav.consumer')}</Link>
              <a href="#" className="hover:text-white transition-colors">{t('businessPage.api.viewDocs')}</a>
              <a href="#" className="hover:text-white transition-colors">{t('footer.privacy')}</a>
              <a href="#" className="hover:text-white transition-colors">{t('footer.terms')}</a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
            © 2025 Wisedrive Technologies. {t('footer.rights')}
          </div>
        </div>
      </footer>
      {demoModalOpen && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 rounded-t-3xl z-10">
            <div className="flex items-center justify-between">
              <div><h2 className="text-xl font-bold">{t('business.demoSuccess')}</h2><p className="text-emerald-100 text-sm">{t('business.demoSuccessDesc')}</p></div>
              <button onClick={resetModal} className="h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X className="h-5 w-5" /></button>
            </div>
          </div>

          <div className="p-6">
            <div className="text-center py-6">
              <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{t('warrantyModal.requestSubmitted')}</h3>

              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6 max-w-md mx-auto">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Phone className="h-6 w-6 text-emerald-600" />
                  <span className="text-lg font-semibold text-emerald-800">{t('warrantyModal.teamWillContact')}</span>
                </div>
                <p className="text-emerald-700 text-sm mb-4">
                </p>
              </div>

              <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mb-6">
                <div className="flex items-center gap-1"><Clock className="h-4 w-4" /><span>{t('warrantyModal.supportHours')}</span></div>
                <div className="flex items-center gap-1"><Phone className="h-4 w-4" /><span>{t('warrantyModal.supportPhone')}: {t('warrantyModal.supportNumber')}</span></div>
              </div>

              <Button onClick={resetModal} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-semibold">{t('warrantyModal.done')}</Button>
            </div>

          </div>
        </div>
      </div>}

    </div>
  );
};

export default Business;
